import { Icon, IconButton, Stack, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { Comment, isComment, Thread, useBulletinBoardDeleteMutation } from 'api/bulletinBoard/bulletinBoard.api'
import { useUser } from 'api/users/users.api'
import classNames from 'classnames'
import { formatDateTimeToLocalizedString } from 'helpers/dateTime.helper/dateTime.helper'
import { getBulletinDisplayUserName, prettyPrintAnySettingValue } from 'helpers/global.helper/global.helper'
import { formatObjectPropertySettingName } from 'helpers/optimizeSettings.helper/optimizeSettings.helper'
import moment from 'moment'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import Like from '../components/Like'
import PostTitle from './components/PostTitle/PostTitle'
import styles from './GeneralPost.module.less'

type GeneralPostProps = {
  post: Comment | Thread
}

function GeneralPost({ post }: GeneralPostProps): ReactElement {
  const { t } = useTranslation()

  const { mutate: bulletinBoardDelete } = useBulletinBoardDeleteMutation()
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)

  const currentSetting = (post as Thread).bulletin_setting
  const currentProperty = (post as Thread).object_property
  const { data: user } = useUser()
  return (
    <>
      {(!currentSetting || (currentSetting && currentProperty)) && (
        <>
          <Grid container direction="column" spacing={1} className={styles.GeneralPost_ThreadInfo}>
            <Grid container alignItems="flex-start" wrap="nowrap">
              <Grid sx={{ display: 'flex', alignItems: 'center' }}>
                {!post.has_read && <Icon className={classNames(`fas fa-circle`, styles.GeneralPost_HaveSeenCircle)} />}
                <Icon className={classNames(`fal fa-comment-alt`, styles.GeneralPost_ThreadIcon)} />
              </Grid>
              <Grid sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                <Typography className={styles.GeneralPost_TimeStampHeader} display="inline">
                  {moment(post.updated_at).isSame(moment(), `day`) ? (
                    <>
                      {t(`Today`)} {moment(post.updated_at).format(` [@] HH:mm:ss`)}
                    </>
                  ) : (
                    moment(post.updated_at).format(`dddd D MMMM [@] HH:mm:ss`)
                  )}
                </Typography>
              </Grid>
              {post.created_by === user?.id && (
                <Grid sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={t(`Delete`)} arrow placement="top">
                    <IconButton
                      className={styles.GeneralPost_DeleteIcon}
                      onClick={() => setConfirmDeleteModalOpen(true)}
                      size="large"
                    >
                      <Icon className="fas fa-trash" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
            <Grid container>
              <Grid size={{ sm: 1, md: 1, lg: 0.8, xl: 0.3 }} />
              <Grid size={11}>
                <PostTitle
                  settingPost={currentSetting !== null && currentSetting !== undefined}
                  title={getBulletinDisplayUserName(post.creator_full_name)}
                />
                {currentSetting && currentProperty ? (
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="body1" display="inline">
                      {t(`{{name}} is active with the value `, {
                        name: formatObjectPropertySettingName(currentProperty),
                      })}
                      <Typography component="span" variant="body1" display="inline" style={{ fontWeight: `bold` }}>
                        {prettyPrintAnySettingValue(currentProperty, currentSetting) + ` `}
                      </Typography>
                      {currentSetting.comment && (
                        <>
                          <Typography component="span" variant="body1" display="inline">
                            {t(`with reason: `)}
                          </Typography>
                          <Typography component="span" variant="body1" display="inline" style={{ fontWeight: `bold` }}>
                            {currentSetting.comment}
                          </Typography>
                        </>
                      )}
                    </Typography>
                    <Typography variant="body1" display="inline">
                      {t(`The setting is valid from`)}{' '}
                      <Typography component="span" variant="body1" display="inline" style={{ fontWeight: `bold` }}>
                        {t(` {{start}} - {{end}}`, {
                          start: formatDateTimeToLocalizedString(moment(currentSetting.start_time).format(), true),
                          end: currentSetting.end_time
                            ? formatDateTimeToLocalizedString(moment(currentSetting.end_time).format(), true)
                            : t(`Until further notice`),
                        })}
                      </Typography>
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body1" style={{ whiteSpace: `pre-line`, paddingTop: `2px` }}>
                    {post.content}
                  </Typography>
                )}
                {isComment(post) && (
                  <Grid sx={{ paddingTop: 0.5 }}>
                    <Like post={post} />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          <ConfirmDialog
            open={confirmDeleteModalOpen}
            onConfirm={() => {
              bulletinBoardDelete({
                id: post.id,
                type: !isComment(post) ? `bulletin_board_threads` : `bulletin_board_comments`,
              })
            }}
            onClose={() => setConfirmDeleteModalOpen(false)}
            textObject={{
              title: t(`Remove post`),
              text: t(`Are you sure you want to remove your post?`),
            }}
          />
        </>
      )}
    </>
  )
}

export default GeneralPost
